/* 
 * SEO Meta Tags:
 * Title: How It Works - Ready-Made & Custom AI Agents | InScius
 * Description: Two paths to automation: Plug & play AI agents for instant activation, or custom solutions tailored to your unique business processes.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Zap, Settings, CheckCircle2, CreditCard, Calendar, Rocket, ArrowRight } from 'lucide-react';

const HowItWorks: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const customSteps = [
    {
      number: '01',
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
      title: t('howItWorks.step1.title'),
      subtitle: t('howItWorks.step1.subtitle'),
      description: t('howItWorks.step1.description'),
      details: [
        t('howItWorks.step1.detail1'),
        t('howItWorks.step1.detail2'),
        t('howItWorks.step1.detail3'),
        t('howItWorks.step1.detail4')
      ],
      duration: t('howItWorks.step1.duration'),
      deliverable: t('howItWorks.step1.deliverable')
    },
    {
      number: '02',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      title: t('howItWorks.step2.title'),
      subtitle: t('howItWorks.step2.subtitle'),
      description: t('howItWorks.step2.description'),
      details: [
        t('howItWorks.step2.detail1'),
        t('howItWorks.step2.detail2'),
        t('howItWorks.step2.detail3'),
        t('howItWorks.step2.detail4')
      ],
      duration: t('howItWorks.step2.duration'),
      deliverable: t('howItWorks.step2.deliverable')
    },
    {
      number: '03',
      icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
      title: t('howItWorks.step3.title'),
      subtitle: t('howItWorks.step3.subtitle'),
      description: t('howItWorks.step3.description'),
      details: [
        t('howItWorks.step3.detail1'),
        t('howItWorks.step3.detail2'),
        t('howItWorks.step3.detail3'),
        t('howItWorks.step3.detail4')
      ],
      duration: t('howItWorks.step3.duration'),
      deliverable: t('howItWorks.step3.deliverable')
    },
    {
      number: '04',
      icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
      title: t('howItWorks.step4.title'),
      subtitle: t('howItWorks.step4.subtitle'),
      description: t('howItWorks.step4.description'),
      details: [
        t('howItWorks.step4.detail1'),
        t('howItWorks.step4.detail2'),
        t('howItWorks.step4.detail3'),
        t('howItWorks.step4.detail4')
      ],
      duration: t('howItWorks.step4.duration'),
      deliverable: t('howItWorks.step4.deliverable')
    }
  ];

  const readyMadeFeatures = t('howItWorks.paths.readyMade.features', { returnObjects: true }) as string[];
  const customFeatures = t('howItWorks.paths.custom.features', { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <Header />

      {/* Hero Section */}
      <main className="relative pt-32 pb-20">
        <section className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-brand-primary/20 border border-brand-secondary/50 rounded-full mb-8 backdrop-blur-sm">
              <div className="w-2 h-2 bg-brand-secondary rounded-full mr-2 animate-pulse"></div>
              <span className="text-brand-secondary text-sm font-medium">{t('howItWorks.hero.badge')}</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-8 text-white leading-tight">
              {t('howItWorks.hero.title')}
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/80 leading-relaxed">
              {t('howItWorks.hero.description')}
            </p>
          </div>

          {/* Two Paths Section */}
          <div className="mb-24">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              {t('howItWorks.paths.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Ready-Made Path */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-3xl blur-xl opacity-25 group-hover:opacity-50 transition duration-500"></div>
                <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-brand-secondary/30 h-full">
                  {/* Badge */}
                  <div className="inline-flex items-center px-3 py-1 bg-brand-secondary/20 border border-brand-secondary/50 rounded-full mb-6">
                    <Zap className="w-4 h-4 text-brand-secondary mr-2" />
                    <span className="text-brand-secondary text-sm font-medium">{t('howItWorks.paths.readyMade.badge')}</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">{t('howItWorks.paths.readyMade.title')}</h3>
                  <p className="text-brand-secondary font-semibold mb-4">{t('howItWorks.paths.readyMade.subtitle')}</p>
                  <p className="text-white/70 mb-6">{t('howItWorks.paths.readyMade.description')}</p>
                  
                  {/* Price */}
                  <div className="mb-6 pb-6 border-b border-white/10">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">{t('howItWorks.paths.readyMade.price')}</span>
                      <span className="text-white/60">{t('howItWorks.paths.readyMade.priceNote')}</span>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {readyMadeFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA */}
                  <Link
                    to="/solutions"
                    className="w-full bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-6 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-brand-secondary/50 hover:scale-105 flex items-center justify-center gap-2"
                  >
                    {t('howItWorks.paths.readyMade.cta')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Custom Path */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-25 group-hover:opacity-50 transition duration-500"></div>
                <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-white/20 h-full">
                  {/* Badge */}
                  <div className="inline-flex items-center px-3 py-1 bg-white/10 border border-white/30 rounded-full mb-6">
                    <Settings className="w-4 h-4 text-white mr-2" />
                    <span className="text-white text-sm font-medium">{t('howItWorks.paths.custom.badge')}</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">{t('howItWorks.paths.custom.title')}</h3>
                  <p className="text-brand-secondary font-semibold mb-4">{t('howItWorks.paths.custom.subtitle')}</p>
                  <p className="text-white/70 mb-6">{t('howItWorks.paths.custom.description')}</p>
                  
                  {/* Price */}
                  <div className="mb-6 pb-6 border-b border-white/10">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">{t('howItWorks.paths.custom.price')}</span>
                      <span className="text-white/60">{t('howItWorks.paths.custom.priceNote')}</span>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {customFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA */}
                  <button
                    onClick={() => navigate('/custom-solutions')}
                    className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-2 border-brand-secondary px-6 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-brand-secondary/40 flex items-center justify-center gap-2"
                  >
                    {t('howItWorks.paths.custom.cta')}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Ready-Made: 3 Simple Steps */}
          <div className="mb-24">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              {t('howItWorks.readyMade.title')}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { icon: CreditCard, step: 'step1' },
                { icon: Calendar, step: 'step2' },
                { icon: Rocket, step: 'step3' }
              ].map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <div key={idx} className="relative">
                    {/* Connector line */}
                    {idx < 2 && (
                      <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-brand-secondary to-brand-primary/30"></div>
                    )}
                    
                    <div className="relative text-center">
                      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-2xl mb-6 shadow-xl shadow-brand-secondary/30">
                        <IconComponent className="w-10 h-10 text-white" />
                      </div>
                      
                      <div className="absolute -top-2 -right-2 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center border-2 border-brand-secondary">
                        <span className="text-brand-secondary font-bold">{t(`howItWorks.readyMade.${item.step}.number`)}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-3">
                        {t(`howItWorks.readyMade.${item.step}.title`)}
                      </h3>
                      <p className="text-white/70">
                        {t(`howItWorks.readyMade.${item.step}.description`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-brand-secondary/50 hover:scale-105"
              >
                {t('howItWorks.paths.readyMade.cta')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Custom Process Timeline */}
          <div className="relative max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              {t('howItWorks.customProcess.title')}
            </h2>
            
            {/* Vertical line for desktop */}
            <div className="hidden lg:block absolute left-1/2 top-24 bottom-0 w-0.5 bg-gradient-to-b from-brand-secondary via-brand-primary to-brand-secondary opacity-30"></div>

            {customSteps.map((step, idx) => (
              <div key={idx} className="relative mb-24 last:mb-0">
                {/* Timeline dot */}
                <div className="hidden lg:block absolute left-1/2 top-8 transform -translate-x-1/2 w-4 h-4 bg-brand-secondary rounded-full border-4 border-slate-900 z-10 shadow-lg shadow-brand-secondary/50"></div>

                <div className={`lg:flex items-start gap-12 ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className="lg:w-1/2">
                    <div className="relative group">
                      {/* Glow effect */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                      
                      <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-brand-secondary/30">
                        {/* Step Number Badge */}
                        <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-brand-secondary to-brand-primary rounded-2xl flex items-center justify-center shadow-xl">
                          <span className="text-white font-bold text-2xl">{step.number}</span>
                        </div>

                        {/* Icon */}
                        <div className="flex items-start gap-6 mb-6 mt-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={step.icon} />
                            </svg>
                          </div>
                          
                          <div>
                            <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                            <p className="text-brand-secondary font-semibold">{step.subtitle}</p>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-white/80 text-lg mb-6 leading-relaxed">
                          {step.description}
                        </p>

                        {/* Details List */}
                        <div className="space-y-3 mb-6">
                          {step.details.map((detail, detailIdx) => (
                            <div key={detailIdx} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 bg-brand-secondary rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-white/70">{detail}</p>
                            </div>
                          ))}
                        </div>

                        {/* Duration & Deliverable */}
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                          <div>
                            <div className="text-white/50 text-sm mb-1">{t('howItWorks.common.duration')}</div>
                            <div className="text-white font-semibold">{step.duration}</div>
                          </div>
                          <div>
                            <div className="text-white/50 text-sm mb-1">{t('howItWorks.common.deliverable')}</div>
                            <div className="text-white font-semibold">{step.deliverable}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Spacer for alternate layout */}
                  <div className="hidden lg:block lg:w-1/2"></div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="relative mt-24 text-center">
            <div className="relative group inline-block">
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition duration-1000"></div>
              
              <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-12 border border-brand-secondary/30">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  {t('howItWorks.cta.title')}
                </h2>
                <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                  {t('howItWorks.cta.description')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/solutions"
                    className="bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-brand-secondary/50 hover:scale-105 flex items-center justify-center gap-2"
                  >
                    {t('howItWorks.cta.browseSolutions')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  
                  <button
                    onClick={() => navigate('/custom-solutions')}
                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-2 border-brand-secondary px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-brand-secondary/40 flex items-center justify-center gap-2"
                  >
                    {t('howItWorks.cta.scheduleConsultation')}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HowItWorks;
